import TextAreaDisplay from 'components/form/Inputs/TextAreaDisplay';
import CrossIcon from 'components/icons/Cross';
import DownloadIcon from 'components/icons/Download';
import RefreshCWIcon from 'components/icons/RefreshCW';
import { FC, useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import MessageType from 'types/Message';
import privateAPI from 'utils/privateAxiosInstance';
import { getLocaleDate, getLocaleTime } from 'utils/time';

const Container = styled.div<{ fromMe: boolean }>`
  display: grid;
  grid-template-columns: 100%;
  margin-top: 10px;

  ${({ fromMe }) =>
    fromMe
      ? css`
          justify-items: right;
          margin-right: 20px;
        `
      : css`
          justify-items: left;
          margin-left: 20px;
        `}
`;

const SenderName = styled.div`
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 2px;
`;

const MessageContainer = styled.div<{ fromMe: boolean }>`
  width: max-content;
  max-width: 90%;
  padding: 8px 12px;
  border-radius: 0.5rem;
  word-break: break-word;

  ${({ fromMe }) =>
    fromMe
      ? css`
          color: white;
          background-color: var(--primary-600);
        `
      : css`
          color: black;
          background-color: var(--gray-200);
        `}
`;

const MessageDate = styled.div`
  font-size: 12px;
  color: var(--gray-500);
`;

type Props = {
  fromMe: boolean;
  message: MessageType;
};

const Message: FC<Props> = ({ fromMe, message }) => {
  return (
    <Container fromMe={fromMe}>
      {!fromMe && <SenderName>{message.sender?.firstName}</SenderName>}
      <MessageContainer fromMe={fromMe}>
        {message.filepath ? (
          <MessageFileProps message={message} />
        ) : (
          <MessageText text={message.text || ''} />
        )}
      </MessageContainer>
      <MessageDate>
        {getLocaleDate(message.createdAt || '')} -{' '}
        {getLocaleTime(message.createdAt || '')}
      </MessageDate>
    </Container>
  );
};

type MessageTextProps = { text: string };
const MessageText: FC<MessageTextProps> = ({ text }) => {
  return <TextAreaDisplay text={text} />;
};

const MessageFileContainer = styled.div`
  & > div {
    display: grid;
    place-items: center;
    width: 13rem;
    max-width: 100%;
    height: 5rem;
    background-color: white;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;

    --border-size: 2.5px;
    --total-width: 2.5rem;
    --svg-width: 1rem;
  }

  p {
    font-size: 0.6rem;
    text-align: end;
  }
  user-select: none;
`;

const DownloadIconContainer = styled.div`
  position: relative;
  width: var(--total-width);
  height: var(--total-width);
  stroke: white;
  cursor: pointer;

  div {
    width: var(--total-width);
    height: var(--total-width);
    background-color: var(--primary-600);
    border: var(--border-size) solid var(--primary-600);
    border-radius: 50rem;
  }

  svg {
    position: absolute;
    width: var(--svg-width);
    height: var(--svg-width);
    top: calc((var(--total-width) - var(--svg-width)) / 2);
    left: calc((var(--total-width) - var(--svg-width)) / 2);
  }
`;

const CrossIconContainer = styled.div`
  position: relative;
  background-color: white;
  stroke: var(--primary-600);
  cursor: pointer;

  div {
    border-radius: 50rem;
    border: var(--border-size) solid var(--primary-600);
    width: var(--total-width);
    height: var(--total-width);
    animation: spin 2s linear infinite;
    border-top-color: transparent;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  svg {
    position: absolute;
    width: var(--svg-width);
    height: var(--svg-width);
    top: calc((var(--total-width) - var(--svg-width)) / 2);
    left: calc((var(--total-width) - var(--svg-width)) / 2);
  }
`;

const ErrorIconContainer = styled.div`
  position: relative;
  stroke: white;
  cursor: pointer;

  div {
    border-radius: 50rem;
    border: var(--border-size) solid var(--error-600);
    background-color: var(--error-600);
    width: var(--total-width);
    height: var(--total-width);
    animation: spin 2s linear infinite;
    border-top-color: transparent;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  svg {
    position: absolute;
    width: var(--svg-width);
    height: var(--svg-width);
    top: calc((var(--total-width) - var(--svg-width)) / 2);
    left: calc((var(--total-width) - var(--svg-width)) / 2);
  }
`;

type MessageFilePropsType = { message: MessageType };
const MessageFileProps: FC<MessageFilePropsType> = ({ message }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const displayedFileName = useMemo(() => {
    const MAX_LENGTH = 35;
    const END_LENGTH = 15;
    if (!message.filename) return '';
    const split = `${message.filename}`.split('.');
    const extension = split[split.length - 1];
    let name = split.slice(0, -1).join('.');
    if (name.length > MAX_LENGTH) {
      name =
        name.slice(0, MAX_LENGTH - END_LENGTH - 5) +
        '.....' +
        name.slice(-END_LENGTH - 1, -1);
    }

    return `${name}.${extension}`;
  }, [message]);

  const [controller, setController] = useState<AbortController | null>(null);
  const handleDownload = () => {
    setError(false);
    setLoading(true);

    const abortController = new AbortController();
    setController(abortController);

    privateAPI
      .get(`/messaging/download-file/${message.id}`, {
        responseType: 'blob',
        signal: abortController.signal,
      })
      .then((res) => {
        try {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', message.filename || '');
          document.body.appendChild(link);
          link.click();
        } catch {
        }
      })
      .catch((err) => {
        if (err.message === 'canceled') return;
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = useCallback(() => {
    controller?.abort();
    setLoading(false);
  }, [controller, setLoading]);

  return (
    <MessageFileContainer>
      <div>
        {error ? (
          <ErrorIconContainer onClick={() => setError(false)}>
            <div />
            <RefreshCWIcon />
          </ErrorIconContainer>
        ) : !loading ? (
          <DownloadIconContainer onClick={handleDownload}>
            <div />
            <DownloadIcon />
          </DownloadIconContainer>
        ) : (
          <CrossIconContainer onClick={handleCancel}>
            <div />
            <CrossIcon />
          </CrossIconContainer>
        )}
      </div>
      <p
        title={
          (message.filename !== displayedFileName && message.filename) ||
          undefined
        }
      >
        {displayedFileName}
      </p>
    </MessageFileContainer>
  );
};

export default Message;
